import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, X } from "lucide-react";
import {
  EVENT_TYPES,
  useApprovals,
  useApprovalsCount,
  useBulkMutation,
  useDecideMutation,
  type ApprovalListItem,
  type ApprovalOrigin,
  type ApprovalOriginChannel,
} from "../lib/approvals";
import { ApprovalHistoryTab } from "../components/ApprovalHistory";
import { EventDetailDrawer } from "../components/EventDetailDrawer";
import { PresendBadge, PresendBulkNotice, PresendNotice, RuleCheckFailedBadge } from "../components/PresendNotice";
import { EmptyState, TableSkeleton } from "../components/states";

function formatRelative(iso: string): string {
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  const m = Math.floor(diff / 60_000);
  if (m < 1) return "vor wenigen Sek.";
  if (m < 60) return `vor ${m} Min.`;
  const h = Math.floor(m / 60);
  if (h < 24) return `vor ${h} Std.`;
  const d = Math.floor(h / 24);
  return `vor ${d} Tag${d === 1 ? "" : "en"}`;
}

function formatDeal(value: number | null, currency: string | null): string {
  if (value === null) return "—";
  return `${value.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency ?? ""}`.trim();
}

/** Page shell: tabs „Offen" (the queue) and „Historie" (decided cases, TASK-209). The tabs are
 *  separate components so the queue's keyboard shortcuts are gone while the history is shown. */
export function ApprovalsPage() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "history" ? "history" : "open";
  const setTab = (next: "open" | "history") => {
    const p = new URLSearchParams(params);
    if (next === "history") p.set("tab", "history");
    else p.delete("tab");
    setParams(p, { replace: true });
  };
  const tabClass = (active: boolean) =>
    `border-b-2 px-3 py-2 text-sm font-medium ${active ? "border-accent text-text-primary" : "border-transparent text-text-secondary hover:text-text-primary"}`;

  return (
    <>
      <div className="px-6 pt-6">
        <div className="mx-auto flex max-w-content gap-2 border-b border-border-primary" role="tablist">
          <button type="button" role="tab" aria-selected={tab === "open"} className={tabClass(tab === "open")} onClick={() => setTab("open")}>
            Offen
          </button>
          <button type="button" role="tab" aria-selected={tab === "history"} className={tabClass(tab === "history")} onClick={() => setTab("history")}>
            Historie
          </button>
        </div>
      </div>
      {tab === "history" ? (
        <div className="pt-4">
          <ApprovalHistoryTab />
        </div>
      ) : (
        <OpenApprovals />
      )}
    </>
  );
}

function OpenApprovals() {
  const [params, setParams] = useSearchParams();
  const eventTypeFilter = params.get("eventType") || undefined;
  const projectFilter = params.get("project") || undefined;
  const gateTypeRaw = params.get("gateType");
  const gateTypeFilter: "approval_rule" | "merge_review" | undefined =
    gateTypeRaw === "approval_rule" || gateTypeRaw === "merge_review"
      ? gateTypeRaw
      : undefined;

  const filters = useMemo(
    () => ({
      project: projectFilter,
      eventType: eventTypeFilter,
      gateType: gateTypeFilter,
    }),
    [projectFilter, eventTypeFilter, gateTypeFilter],
  );

  const { data, isLoading } = useApprovals(filters);
  const totalAll = useApprovalsCount();
  const decide = useDecideMutation();
  const bulk = useBulkMutation();

  const items = data?.items ?? [];

  const [cursor, setCursor] = useState(0);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [rejectingId, setRejectingId] = useState<number | null>(null);
  const [rejectComment, setRejectComment] = useState("");
  const [bulkRejectOpen, setBulkRejectOpen] = useState(false);

  const rowRefs = useRef<Map<number, HTMLTableRowElement>>(new Map());

  // Refs mirror latest state so the keyboard handler can read fresh values
  // without re-registering on every render. Re-registering creates brief
  // gaps where keystrokes can hit a stale closure (capped at the previous
  // items.length, hence "stops at vorletzten").
  const itemsRef = useRef(items);
  const cursorRef = useRef(cursor);
  const drawerOpenRef = useRef(drawerOpen);
  const rejectingIdRef = useRef(rejectingId);
  const bulkRejectOpenRef = useRef(false);
  const selectedRef = useRef(selected);
  itemsRef.current = items;
  cursorRef.current = cursor;
  drawerOpenRef.current = drawerOpen;
  rejectingIdRef.current = rejectingId;
  bulkRejectOpenRef.current = bulkRejectOpen;
  selectedRef.current = selected;

  // Reset cursor if items change
  useEffect(() => {
    if (cursor >= items.length) setCursor(Math.max(0, items.length - 1));
  }, [items.length, cursor]);

  // Scroll cursor into view
  useEffect(() => {
    const id = items[cursor]?.id;
    if (id !== undefined) {
      const row = rowRefs.current.get(id);
      row?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [cursor, items]);

  const updateFilter = (
    key: "project" | "eventType" | "gateType",
    value: string | null,
  ) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const toggleSelected = (id: number) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleApprove = (id: number, droppedKeys?: string[]) => {
    decide.mutate(
      { id, action: "approve", droppedKeys },
      {
        onSuccess: () => {
          if (droppedKeys && droppedKeys.length > 0) {
            toast.success(
              `Genehmigt — ohne ${droppedKeys.length} Key${droppedKeys.length === 1 ? "" : "s"}`,
            );
          } else {
            toast.success("Genehmigt");
          }
        },
        onError: (err) =>
          toast.error(`Genehmigung fehlgeschlagen: ${(err as Error).message}`),
      },
    );
    setDrawerOpen(false);
  };

  const handleReject = (id: number, comment?: string) => {
    decide.mutate(
      { id, action: "reject", comment },
      {
        onSuccess: () => toast.success("Abgelehnt"),
        onError: (err) =>
          toast.error(`Ablehnung fehlgeschlagen: ${(err as Error).message}`),
      },
    );
    setRejectingId(null);
    setRejectComment("");
    setDrawerOpen(false);
  };

  const presendInSelection = items.filter(
    (it) => selected.has(it.id) && it.capiPresendAt,
  ).length;

  const handleBulk = (action: "approve" | "reject", confirmed = false) => {
    if (selected.size === 0) return;
    // A rejected purchase that was already handed over to Meta/Google stays
    // there: ask before the bulk reject runs (TASK-208 review F7).
    if (action === "reject" && presendInSelection > 0 && !confirmed) {
      setBulkRejectOpen(true);
      return;
    }
    setBulkRejectOpen(false);
    const ids = Array.from(selected);
    bulk.mutate(
      { ids, action },
      {
        onSuccess: (res) =>
          toast.success(
            `${res.updatedCount} ${action === "approve" ? "genehmigt" : "abgelehnt"}` +
              (res.skippedCount ? ` (${res.skippedCount} übersprungen)` : ""),
          ),
        onError: () => toast.error("Bulk-Aktion fehlgeschlagen"),
      },
    );
    setSelected(new Set());
  };

  // Keyboard shortcuts — registered ONCE. The handler reads from refs so
  // it always has the latest items/cursor without re-registering.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.tagName === "SELECT" ||
          target.isContentEditable)
      ) {
        return;
      }
      if (rejectingIdRef.current !== null || bulkRejectOpenRef.current) return; // reject dialog open

      const items = itemsRef.current;
      const cursor = cursorRef.current;

      // Use e.code (keyboard-position) so Shift+J still resolves to "KeyJ".
      const isJ = e.code === "KeyJ";
      const isK = e.code === "KeyK";
      const isA = e.code === "KeyA";
      const isR = e.code === "KeyR";

      if (isJ || e.key === "ArrowDown") {
        e.preventDefault();
        window.getSelection()?.removeAllRanges();
        if (e.shiftKey) {
          const cur = items[cursor];
          if (cur) toggleSelected(cur.id);
        }
        setCursor((c) => Math.min(itemsRef.current.length - 1, c + 1));
      } else if (isK || e.key === "ArrowUp") {
        e.preventDefault();
        window.getSelection()?.removeAllRanges();
        if (e.shiftKey) {
          const cur = items[cursor];
          if (cur) toggleSelected(cur.id);
        }
        setCursor((c) => Math.max(0, c - 1));
      } else if (isA && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setSelected(new Set(itemsRef.current.map((it) => it.id)));
      } else if (isA && !e.shiftKey) {
        const cur = items[cursor];
        if (cur) {
          e.preventDefault();
          handleApprove(cur.id);
        }
      } else if (isR && !e.shiftKey) {
        const cur = items[cursor];
        if (cur) {
          e.preventDefault();
          setRejectingId(cur.id);
        }
      } else if (e.key === "Enter") {
        // Enter on a button or a tab (tab strip) activates that control — not the drawer.
        if (target?.closest('button, [role="tab"], [role="tablist"]')) return;
        if (items[cursor]) {
          e.preventDefault();
          setDrawerOpen(true);
        }
      } else if (e.key === "Escape") {
        if (drawerOpenRef.current) setDrawerOpen(false);
        else if (selectedRef.current.size > 0) setSelected(new Set());
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const drawerItem = items[cursor] ?? null;

  return (
    <div className="px-6 py-8">
      <div className="mx-auto max-w-content space-y-6">
        <header className="flex items-baseline justify-between">
          <div>
            <p className="font-mono text-xs uppercase tracking-wider text-text-tertiary">
              Approvals
            </p>
            <h1 className="mt-1 text-2xl font-semibold tracking-tight text-text-primary">
              Pending Queue
              <span className="ml-3 font-mono text-base font-normal tabular-nums text-text-secondary">
                {totalAll.data?.count ?? 0}
              </span>
            </h1>
          </div>
          <KeyboardLegend />
        </header>

        <div className="flex flex-wrap items-center gap-2">
          <Filter
            label="Event-Typ"
            value={eventTypeFilter ?? ""}
            options={[
              { v: "", label: "Alle Typen" },
              ...EVENT_TYPES.map((t) => ({ v: t, label: t })),
            ]}
            onChange={(v) => updateFilter("eventType", v || null)}
          />
          <Filter
            label="Gate-Typ"
            value={gateTypeFilter ?? ""}
            options={[
              { v: "", label: "Alle Gates" },
              { v: "approval_rule", label: "Approval-Rule" },
              { v: "merge_review", label: "Merge-Review" },
            ]}
            onChange={(v) => updateFilter("gateType", v || null)}
          />
        </div>

        {selected.size > 0 && (
          <div className="sticky top-0 z-sticky flex items-center justify-between rounded-md border border-accent bg-accent-subtle px-4 py-2 text-sm">
            <span className="font-medium text-text-primary">
              {selected.size} ausgewählt
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setSelected(new Set())}
                className="rounded-md px-2 py-1 text-text-secondary hover:text-text-primary"
              >
                Auswahl leeren
              </button>
              <button
                type="button"
                onClick={() => handleBulk("reject")}
                className="rounded-md border border-border-primary bg-bg-primary px-3 py-1 font-medium text-text-primary hover:bg-bg-hover"
              >
                Bulk Ablehnen
              </button>
              <button
                type="button"
                onClick={() => handleBulk("approve")}
                className="rounded-md bg-accent px-3 py-1 font-medium text-text-on-accent hover:bg-accent-hover"
              >
                Bulk Genehmigen
              </button>
            </div>
          </div>
        )}

        {isLoading ? (
          <TableSkeleton rows={6} />
        ) : items.length === 0 ? (
          <EmptyState
            icon={<CheckCircle2 size={20} className="text-success" />}
            title="Alles erledigt"
            body="Keine Events warten auf Approval."
          />
        ) : (
          <div className="overflow-hidden rounded-md border border-border-primary">
            <table className="w-full text-sm">
              <thead className="border-b border-border-primary bg-bg-secondary text-left">
                <tr>
                  <th className="w-10 px-3 py-2.5">
                    <input
                      type="checkbox"
                      aria-label="Alle auswählen"
                      checked={
                        selected.size === items.length && items.length > 0
                      }
                      onChange={(e) =>
                        setSelected(
                          e.target.checked
                            ? new Set(items.map((it) => it.id))
                            : new Set(),
                        )
                      }
                    />
                  </th>
                  <ColHead>Typ</ColHead>
                  <ColHead>Projekt</ColHead>
                  <ColHead>Vorname</ColHead>
                  <ColHead>Nachname</ColHead>
                  <ColHead>Email</ColHead>
                  <ColHead>Herkunft</ColHead>
                  <ColHead>Produkt</ColHead>
                  <ColHead align="right">Wert</ColHead>
                  <ColHead>Erstellt</ColHead>
                  <ColHead align="right">Aktionen</ColHead>
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => (
                  <Row
                    key={it.id}
                    item={it}
                    isCursor={idx === cursor}
                    isSelected={selected.has(it.id)}
                    onCursor={() => setCursor(idx)}
                    onToggle={() => toggleSelected(it.id)}
                    onOpen={() => setDrawerOpen(true)}
                    onApprove={() => handleApprove(it.id)}
                    onReject={() => setRejectingId(it.id)}
                    rowRef={(el) => {
                      if (el) rowRefs.current.set(it.id, el);
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <EventDetailDrawer
        item={drawerItem}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        onApprove={handleApprove}
        onReject={(id) => setRejectingId(id)}
      />

      {bulkRejectOpen && (
        <BulkRejectDialog
          presendCount={presendInSelection}
          total={selected.size}
          onCancel={() => setBulkRejectOpen(false)}
          onConfirm={() => handleBulk("reject", true)}
        />
      )}

      {rejectingId !== null && (
        <RejectDialog
          presendAt={items.find((it) => it.id === rejectingId)?.capiPresendAt ?? null}
          comment={rejectComment}
          onCommentChange={setRejectComment}
          onCancel={() => {
            setRejectingId(null);
            setRejectComment("");
          }}
          onConfirm={() => handleReject(rejectingId, rejectComment)}
        />
      )}
    </div>
  );

  function ColHead({
    children,
    align,
  }: {
    children: React.ReactNode;
    align?: "right";
  }) {
    return (
      <th
        className={`px-3 py-2.5 font-medium text-text-secondary ${align === "right" ? "text-right" : ""}`}
      >
        {children}
      </th>
    );
  }
}

interface RowProps {
  item: ApprovalListItem;
  isCursor: boolean;
  isSelected: boolean;
  onCursor: () => void;
  onToggle: () => void;
  onOpen: () => void;
  onApprove: () => void;
  onReject: () => void;
  rowRef: (el: HTMLTableRowElement | null) => void;
}

function Row({
  item,
  isCursor,
  isSelected,
  onCursor,
  onToggle,
  onOpen,
  onApprove,
  onReject,
  rowRef,
}: RowProps) {
  return (
    <tr
      ref={rowRef}
      onClick={onCursor}
      className={`border-b border-border-secondary last:border-0 transition-colors ${
        isSelected ? "bg-accent-subtle" : ""
      } ${isCursor ? "border-l-2 border-l-accent" : "border-l-2 border-l-transparent"} hover:bg-bg-hover`}
    >
      <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          aria-label={`Event ${item.id} auswählen`}
          checked={isSelected}
          onChange={onToggle}
        />
      </td>
      <td className="px-3 py-2.5">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onOpen}
            className="font-medium text-text-primary hover:text-text-link-hover"
          >
            {item.eventType}
          </button>
          <PresendBadge at={item.capiPresendAt} />
          <RuleCheckFailedBadge failed={item.ruleCheckFailed} />
          {item.gateType === "merge_review" && (
            <span
              title={
                item.affectedEventCount > 1
                  ? `Merge-Review — ${item.affectedEventCount} betroffene Events`
                  : "Merge-Review-Gate"
              }
              className="rounded-full bg-warning-bg px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-warning"
            >
              Merge
              {item.affectedEventCount > 1 ? ` ·${item.affectedEventCount}×` : ""}
            </span>
          )}
        </div>
      </td>
      <td className="px-3 py-2.5 text-text-secondary">{item.projectName}</td>
      <td className="px-3 py-2.5 text-text-secondary">{item.firstName ?? "—"}</td>
      <td className="px-3 py-2.5 text-text-secondary">{item.lastName ?? "—"}</td>
      <td className="px-3 py-2.5 font-mono text-xs text-text-secondary">
        {item.email ?? "—"}
      </td>
      <td className="px-3 py-2.5">
        <OriginCell origin={item.origin} />
      </td>
      {/* Single line, truncated — real product names run up to ~65 chars and
          several cart items arrive comma-joined; the full text stays readable
          on hover and in the detail drawer. */}
      <td className="max-w-[12rem] truncate px-3 py-2.5 text-text-secondary">
        {item.productName ? (
          <span title={item.productName}>{item.productName}</span>
        ) : (
          "—"
        )}
      </td>
      <td className="px-3 py-2.5 text-right font-mono tabular-nums text-text-primary">
        {formatDeal(item.dealValue, item.currency)}
      </td>
      <td className="px-3 py-2.5 text-text-secondary">
        {formatRelative(item.createdAt)}
      </td>
      <td className="px-3 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
        <div className="inline-flex gap-1">
          <button
            type="button"
            onClick={onReject}
            className="rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-xs font-medium text-text-secondary transition-colors hover:bg-danger-bg hover:text-danger"
          >
            Ablehnen
          </button>
          <button
            type="button"
            onClick={onApprove}
            className="rounded-md bg-accent px-2 py-1 text-xs font-medium text-text-on-accent transition-colors hover:bg-accent-hover"
          >
            Genehmigen
          </button>
        </div>
      </td>
    </tr>
  );
}

/** Kanal-Kürzel je Einstufung — deutsch, wie in der Freigabe-Maske sonst auch. */
function originLabel(channel: ApprovalOriginChannel): string {
  switch (channel) {
    case "meta_ads":
      return "Meta Ads";
    case "google_ads":
      return "Google Ads";
    case "organic":
      return "Organic";
    case "unattributed":
      return "ohne Zuordnung";
    case "broken":
      return "Herkunft defekt";
    case "unknown":
      return "unbekannt";
  }
}

function OriginCell({ origin }: { origin: ApprovalOrigin }) {
  const label = originLabel(origin.channel);
  if (origin.channel === "broken") {
    return (
      <span
        title="Herkunftsfelder enthalten unaufgelöste Platzhalter (z. B. {{campaign.name}}) — kein verwertbarer Kampagnenname"
        className="rounded-full bg-warning-bg px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-warning"
      >
        {label}
      </span>
    );
  }
  if (origin.channel === "unattributed" || origin.channel === "unknown") {
    return <span className="text-text-tertiary">{label}</span>;
  }
  return (
    <span className="text-text-secondary">
      {label}
      {origin.campaignName ? ` · ${origin.campaignName}` : ""}
    </span>
  );
}

function Filter({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { v: string; label: string }[];
  onChange: (v: string) => void;
}) {
  return (
    <label className="inline-flex items-center gap-2 text-sm">
      <span className="font-medium text-text-secondary">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 rounded-md border border-border-primary bg-bg-primary px-2 text-text-primary focus:border-border-focus focus:outline-none"
      >
        {options.map((o) => (
          <option key={o.v} value={o.v}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function KeyboardLegend() {
  return (
    <div className="hidden gap-3 font-mono text-xs text-text-tertiary md:flex">
      <Hint k="J / K" l="Cursor" />
      <Hint k="A" l="Approve" />
      <Hint k="R" l="Reject" />
      <Hint k="⏎" l="Detail" />
      <Hint k="⌘ A" l="Alle wählen" />
    </div>
  );
}

function Hint({ k, l }: { k: string; l: string }) {
  return (
    <span>
      <kbd className="rounded border border-border-primary bg-bg-secondary px-1.5 py-0.5">
        {k}
      </kbd>{" "}
      {l}
    </span>
  );
}

export function BulkRejectDialog({
  presendCount,
  total,
  onCancel,
  onConfirm,
}: {
  presendCount: number;
  total: number;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="fixed inset-0 z-modal flex items-center justify-center bg-overlay px-4 animate-fade-in">
      <div className="w-full max-w-md rounded-md border border-border-primary bg-bg-elevated shadow-overlay">
        <div className="border-b border-border-primary px-5 py-3">
          <p className="font-medium text-text-primary">{total} Events ablehnen</p>
        </div>
        <div className="space-y-3 px-5 py-4">
          <PresendBulkNotice count={presendCount} total={total} />
        </div>
        <div className="flex justify-end gap-2 border-t border-border-primary bg-bg-secondary px-5 py-3">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-border-primary bg-bg-primary px-3 py-1.5 text-sm font-medium text-text-primary hover:bg-bg-hover"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={onConfirm}
            autoFocus
            className="rounded-md bg-danger px-3 py-1.5 text-sm font-medium text-text-inverse hover:opacity-90"
          >
            Ablehnen
          </button>
        </div>
      </div>
    </div>
  );
}

export function RejectDialog({
  presendAt,
  comment,
  onCommentChange,
  onCancel,
  onConfirm,
}: {
  presendAt: string | null;
  comment: string;
  onCommentChange: (v: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="fixed inset-0 z-modal flex items-center justify-center bg-overlay px-4 animate-fade-in">
      <div className="w-full max-w-md rounded-md border border-border-primary bg-bg-elevated shadow-overlay">
        <div className="flex items-center justify-between border-b border-border-primary px-5 py-3">
          <p className="font-medium text-text-primary">Event ablehnen</p>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Schließen"
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-text-secondary hover:bg-bg-hover"
          >
            <X size={14} />
          </button>
        </div>
        <div className="space-y-3 px-5 py-4">
          <PresendNotice at={presendAt} />
          <label className="block text-sm font-medium text-text-secondary">
            Kommentar (optional)
          </label>
          <textarea
            value={comment}
            onChange={(e) => onCommentChange(e.target.value)}
            rows={3}
            placeholder="z.B. Spam, Test-Daten, ungewöhnlicher Wert…"
            className="mt-2 w-full rounded-md border border-border-primary bg-bg-primary px-3 py-2 text-sm text-text-primary focus:border-border-focus focus:outline-none"
          />
        </div>
        <div className="flex justify-end gap-2 border-t border-border-primary bg-bg-secondary px-5 py-3">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-border-primary bg-bg-primary px-3 py-1.5 text-sm font-medium text-text-primary hover:bg-bg-hover"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={onConfirm}
            autoFocus
            className="rounded-md bg-danger px-3 py-1.5 text-sm font-medium text-text-inverse hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-danger focus:ring-offset-2 focus:ring-offset-bg-secondary"
          >
            Ablehnen
          </button>
        </div>
      </div>
    </div>
  );
}
